import React, { useState, useEffect } from 'react';
import { Tabs, Spin, Alert, Button, message } from 'antd';
import api from '../../../services/api';
import axios from 'axios';
import PasswordPolicy from './PasswordPolicy';
import TwoFactorAuth from './TwoFactorAuth';
import SessionSettings from './SessionSettings';
import SecurityNotifications from './SecurityNotifications';
import LicenseEncryption from './LicenseEncryption';

const { TabPane } = Tabs;

const SecuritySettings = () => {
  const [messageApi, contextHolder] = message.useMessage();
  
  const showMessage = (type, content) => {
    messageApi[type](content);
  };
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({
    password_policy: {},
    two_factor_auth: {},
    session: {},
    notifications: {},
    license_encryption: {}
  });
  
  /**
   * Fetches security settings from the server
   * @returns {AbortController} The abort controller for the request
   */
  const fetchSecuritySettings = async () => {
    const controller = new AbortController();
    
    console.debug('Fetching security settings...');
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all security settings including password policy and two-factor auth
      const [securitySettings, passwordPolicy, twoFactorAuth] = await Promise.all([
        api.settings.getSecuritySettings(controller.signal),
        api.settings.passwordPolicy.get(controller.signal),
        api.settings.twoFactorAuth.get(controller.signal)
      ]);
      
      if (!securitySettings || !passwordPolicy || !twoFactorAuth) {
        throw new Error('Failed to load security settings');
      }
      
      console.debug('Security settings loaded successfully');
      
      // Normalize and merge settings with defaults
      setSettings(prev => ({
        ...prev,
        ...securitySettings,
        // Password policy settings from dedicated endpoint
        password_policy: {
          min_length: 8,
          require_uppercase: true,
          require_lowercase: true,
          require_numbers: true,
          require_special_char: true,
          max_age_days: 90,
          history_size: 5,
          max_attempts: 5,
          lockout_duration_minutes: 15,
          ...passwordPolicy
        },
        two_factor_auth: {
          enabled: false,
          required_for_admins: true,
          required_for_all: false,
          allowed_methods: ['Email'],
          remember_days: 30,
          ...twoFactorAuth
        },
        session: {
          timeout_minutes: 30,
          concurrent_sessions: 1,
          ...(securitySettings.session || {})
        },
        notifications: {
          security_alerts: true,
          login_attempts: true,
          ...(securitySettings.notifications || {})
        }
      }));
      
      setInitialLoad(false);
    } catch (err) {
      // Don't show error if request was aborted (e.g., component unmounted)
      if (axios.isCancel(err)) {
        console.debug('Request canceled:', err.message);
        return controller;
      }
      
      console.error('Error fetching security settings:', err);
      
      const errorMessage = err.response?.data?.message || 
                         err.message || 
                         'Failed to load security settings. Please try again later.';
      
      setError(errorMessage);
      showMessage('error', errorMessage);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
    
    return controller; // Return the controller for cleanup
  };
  
  useEffect(() => {
    let controller;
    
    const loadData = async () => {
      controller = await fetchSecuritySettings();
    };
    
    loadData();
    
    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, []);
  
  const handleTabChange = (key) => {
    setActiveTab(parseInt(key));
  };
  
  const handleUpdateSetting = async (settingKey, data) => {
    try {
      // Special handling for password policy updates
      if (settingKey === 'password_policy') {
        const updatedPolicy = await api.settings.passwordPolicy.update(data);
        
        // Optimistically update the UI
        setSettings(prev => ({
          ...prev,
          password_policy: {
            ...prev.password_policy,
            ...updatedPolicy
          }
        }));
        
        showMessage('success', 'Password Policy updated successfully');
        return true;
      }
      
      // Special handling for two-factor authentication
      if (settingKey === 'two_factor_auth') {
        const updatedSettings = await api.settings.twoFactorAuth.update(data);
        
        // Optimistically update the UI
        setSettings(prev => ({
          ...prev,
          two_factor_auth: {
            ...prev.two_factor_auth,
            ...updatedSettings
          }
        }));
        
        showMessage('success', 'Two-Factor Authentication settings updated successfully');
        return true;
      }
      
      // Handle other setting types
      const endpoints = {
        session: '/security/session-settings',
        notifications: '/security/notifications'
      };
      
      const endpoint = endpoints[settingKey];
      if (!endpoint) {
        throw new Error('Invalid setting key');
      }
      
      // Add updated_by field if not present
      const payload = {
        ...data,
        updated_by: data.updated_by || localStorage.getItem('userId') || 'system'
      };
      
      await axios.put(`/api/v1/settings${endpoint}`, payload);
      
      // Optimistically update the UI for other settings
      setSettings(prev => ({
        ...prev,
        [settingKey]: {
          ...prev[settingKey],
          ...payload
        }
      }));
      
      // Show success message with the updated setting name
      const settingNames = {
        session: 'Session Settings',
        notifications: 'Security Notifications'
      };
      
      showMessage('success', `${settingNames[settingKey] || 'Settings'} updated successfully`);
      return true;
    } catch (err) {
      console.error(`Error updating ${settingKey}:`, err);
      const errorMessage = err.response?.data?.message || 
                         err.response?.data?.detail || 
                         `Failed to update ${settingKey}. Please try again.`;
      
      showMessage('error', errorMessage);
      
      // Re-fetch the latest settings to ensure UI consistency
      fetchSecuritySettings();
      
      return false;
    }
  };
  
  if (loading && !settings.password_policy) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }
  
  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {contextHolder}
      {error && (
        <div style={{ marginBottom: '24px' }}>
          <div className="ant-alert ant-alert-error">
            <div className="ant-alert-content">
              <div className="ant-alert-message">{error}</div>
            </div>
          </div>
        </div>
      )}
      
      <Tabs
        activeKey={activeTab.toString()}
        onChange={handleTabChange}
        type="card"
        tabBarStyle={{ marginBottom: '24px' }}
        tabBarGutter={8}
        destroyInactiveTabPane={true}
        items={[
          {
            key: '0',
            label: 'Password Policy',
            forceRender: false
          },
          {
            key: '1',
            label: 'Two-Factor Authentication',
            forceRender: false
          },
          
          {
            key: '2',
            label: 'Security Notifications',
            forceRender: false
          },
        {
            key: '3',
            label: 'License Encryption',
            forceRender: false
          }
        ]}
      />
      
      <div style={{ padding: '0 0 24px 0' }}>
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
            action={
              <Button 
                size="small" 
                type="text"
                onClick={fetchSecuritySettings}
                icon={<span>⟳</span>}
              >
                Retry
              </Button>
            }
          />
        )}
        
        {initialLoad && loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spin size="large" tip="Loading security settings..." />
          </div>
        ) : (
          <>
            {activeTab === 0 && (
              <PasswordPolicy 
                settings={settings.password_policy || {}} 
                onUpdate={(data) => handleUpdateSetting('password_policy', data)} 
                loading={loading}
              />
            )}
            
            {activeTab === 1 && (
              <TwoFactorAuth 
                settings={settings.two_factor_auth || {}} 
                onUpdate={(data) => handleUpdateSetting('two_factor_auth', data)} 
                loading={loading}
              />
            )}
            
            {/* {activeTab === 2 && (
              <SessionSettings 
                settings={settings.session || {}} 
                onUpdate={(data) => handleUpdateSetting('session', data)} 
                loading={loading}
              />
            )} */}
            
            {activeTab === 2 && (
              <SecurityNotifications 
                settings={settings.notifications || {}} 
                onUpdate={(data) => handleUpdateSetting('notifications', data)} 
                loading={loading}
              />
            )}
            
            {activeTab === 3 && (
              <LicenseEncryption 
                settings={settings.license_encryption || {}} 
                onUpdate={async (data) => {
                  try {
                    // If no data is provided, just refresh the settings
                    if (!data) {
                      const settings = await api.settings.getLicenseEncryptionSettings();
                      setSettings(prev => ({
                        ...prev,
                        license_encryption: settings
                      }));
                      return true;
                    }
                    
                    // Otherwise update the settings
                    const updated = await api.settings.updateLicenseEncryptionSettings(data);
                    setSettings(prev => ({
                      ...prev,
                      license_encryption: {
                        ...prev.license_encryption,
                        ...updated
                      }
                    }));
                    return true;
                  } catch (error) {
                    console.error('Error updating license encryption settings:', error);
                    const errorMessage = error.response?.data?.message || 
                                      error.message || 
                                      'Failed to update license encryption settings';
                    showMessage('error', errorMessage);
                    return false;
                  }
                }} 
                loading={loading}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SecuritySettings;
